import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { UserProvider } from './hooks/useUser';
import Home from './components/Home';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Register from './components/Register';
import ParticipantRegister from './components/ParticipantRegister';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import QuizPlayer from './components/QuizPlayer';
import QuestionnairePreviewPage from './components/QuestionnairePreviewPage';
import QuestionnaireEditor from './components/QuestionnaireEditor';
import authService from './services/authService';
import ReportViewer from './components/ReportViewer';
import './styles/buttons.css';
import './App.css';

// Main App Component with routing logic
function AppContent() {
  const [user, setUser] = useState(null);
  const location = useLocation();

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = authService.getToken();
    const userInfo = authService.getCurrentUser();
    
    if (token && userInfo) {
      setUser(userInfo);
    }

    // Listen for auth logout events (401 errors)
    const handleAuthLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth-logout', handleAuthLogout);

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    authService.logout();
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#2E86AB',
          colorPrimaryHover: '#3a9bc1',
          colorPrimaryActive: '#267a96',
          colorLink: '#2E86AB',
          colorLinkHover: '#3a9bc1',
          colorLinkActive: '#267a96',
        },
        components: {
          Button: {
            colorPrimary: '#2E86AB',
            colorPrimaryHover: '#3a9bc1',
            colorPrimaryActive: '#267a96',
            primaryColor: '#ffffff',
            defaultBg: '#2E86AB',
            defaultColor: '#ffffff',
            defaultBorderColor: '#2E86AB',
          },
          Message: {
            zIndexPopup: 1050,
          },
        },
      }}
      modal={{
        styles: {
          mask: {
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      message={{
        duration: 10,
        maxCount: 3,
        top: 100,
      }}
    >
      <AntApp>
        <Routes>
          {/* Public routes */}
          <Route path="/register" element={<Register />} />
          <Route path="/register/participant/:token" element={<ParticipantRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/quiz/:questionnaireId" element={<QuizPlayer />} />
                  {/* Campaign Quiz Route - uses QuestionnairePreviewPage */}
        <Route path="/campaign-quiz/:targetId" element={<QuestionnairePreviewPage />} />
          <Route path="/preview/:id" element={<QuestionnairePreviewPage />} />
          <Route path="/edit/:id" element={
            user ? <QuestionnaireEditor /> : <Navigate to="/login" replace />
          } />
          <Route path="/login" element={
            user ? <Navigate to="/dashboard" replace /> : <Login onLoginSuccess={handleLoginSuccess} />
          } />
          
          {/* Protected routes */}
          <Route path="/dashboard/*" element={
            user ? (
              <Dashboard 
                user={user}
                onLogout={handleLogout}
                onLoginSuccess={handleLoginSuccess}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          
          {/* Default route */}
          <Route path="/" element={
            user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
          
          {/* Standalone report viewer (opens in new tab) */}
          <Route path="/report-view" element={<ReportViewer />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AntApp>
    </ConfigProvider>
  );
}

// Main App wrapper with Router
function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <div className="App">
          <AppContent />
        </div>
      </UserProvider>
    </BrowserRouter>
  );
}

export default App;
